// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';

/**
 * Returns a safe `<img src>` for a possibly-untrusted image reference, defeating
 * DOM-based XSS (`js/xss-through-dom` / `js/xss-through-exception`) on the src sink:
 *
 * - `data:image/*` (incl. `svg+xml`) is decoded into a same-origin `blob:` object
 *   URL. The src is then a blob reference rather than the raw, attacker-influenced
 *   string, so it can never be reinterpreted as markup. (SVG loaded via `<img>` is
 *   non-scripting in browsers, so previewing it this way is safe.)
 * - `http(s)` URLs are validated with the URL parser and passed through.
 * - Anything else (`javascript:`, `data:text/html`, malformed, …) yields `''`.
 *
 * The created object URL is revoked on change/unmount to avoid leaks.
 */
const IMAGE_DATA_URI = /^data:image\/(png|jpe?g|gif|webp|svg\+xml)[;,]/i;

export function useSafeImageSrc(raw?: string | null): string {
  const [src, setSrc] = useState('');

  useEffect(() => {
    if (!raw) {
      setSrc('');
      return undefined;
    }

    if (/^https?:\/\//i.test(raw)) {
      try {
        const url = new URL(raw);
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          setSrc(url.href);
          return undefined;
        }
      } catch {
        // fall through to reject
      }
      setSrc('');
      return undefined;
    }

    if (IMAGE_DATA_URI.test(raw) && typeof URL.createObjectURL === 'function') {
      try {
        const comma = raw.indexOf(',');
        const meta = raw.substring('data:'.length, comma);
        const mime = meta.split(';')[0];
        const payload = raw.substring(comma + 1);
        const bytes = /;base64/i.test(meta)
          ? Uint8Array.from(atob(payload), (c) => c.charCodeAt(0))
          : new TextEncoder().encode(decodeURIComponent(payload));
        const objectUrl = URL.createObjectURL(new Blob([bytes], { type: mime }));
        setSrc(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
      } catch {
        setSrc('');
        return undefined;
      }
    }

    setSrc('');
    return undefined;
  }, [raw]);

  return src;
}
